import { getOrgPlaybooksEditSessionsByEditSessionId } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { EditPlaybookResponse, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { useEffect, useRef } from 'react';
import useStore, { usePlaybookBuilderStore } from '@/components/playbook-builder/hooks/use-store';
import { versionPrerequisiteIds } from '@/components/playbook-builder/utils/prerequisites';
import { createEditSessionStore } from '@/hooks/use-in-progress-edit-session-storage';
type AdoptOptions = {
  silent?: boolean;
  onlyIfCurrent?: boolean;
};
export type AdoptResult = EditPlaybookResponse | 'stale' | undefined;
const saveResumeHint = (data: EditPlaybookResponse) => {
  const hints = createEditSessionStore(data.playbookId).getState();
  const existing = hints.data[data.startVersionId];
  if (!existing || existing === data.id) {
    hints.update(data.startVersionId, data.id);
  }
};
const useAdoptEditSession = (playbookVersions: PlaybookVersionNoGraph[]) => {
  const store = useStore();
  const errorToast = useRequestErrorToast();
  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const adoptEditSession = async (editSessionId: string, opts?: AdoptOptions): Promise<AdoptResult> => {
    requestSeqRef.current += 1;
    const requestSeq = requestSeqRef.current;
    const graphIdBefore = usePlaybookBuilderStore.getState().data.graph.id;
    try {
      const {
        data
      } = await getOrgPlaybooksEditSessionsByEditSessionId({
        path: {
          editSessionId
        },
        throwOnError: true
      });
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return undefined;
      const current = usePlaybookBuilderStore.getState().data;
      if (opts?.onlyIfCurrent && current.sessionId !== data.id) return undefined;
      if (current.sessionId === data.id && current.graph.id !== graphIdBefore) return 'stale';
      if (current.sessionId !== data.id || current.graph.id !== data.graph.id) {
        // The drawers flag what this edit newly requires or stops publishing, which only means
        // anything against the version the session started from. That isn't always the version the
        // page loaded: a Percy deep link can carry a session for a different one. Leaving a baseline
        // unknown beats assuming empty, which would flag every field.
        const base = playbookVersions.find(version => version.id === data.startVersionId);
        store.setGraphUpdate(data, {
          baseRequiredPrerequisites: base && versionPrerequisiteIds([base]),
          baseOutputs: base?.outputs
        });
      }
      saveResumeHint(data);
      return data;
    } catch (error) {
      if (!opts?.silent) errorToast(error);
      return undefined;
    }
  };
  return adoptEditSession;
};
export default useAdoptEditSession;